import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/data/content";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="nav-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, ease: [0.19, 1, 0.22, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-black/60 backdrop-blur-xl border-b border-[var(--line)]" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-[1500px] items-center justify-between px-6 py-5 md:px-10">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="font-display text-xl tracking-tight text-[var(--text)]"
        >
          Mehretina<span className="text-accent">.</span>
        </button>

        <div className="hidden items-center gap-10 md:flex">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => go(l.href)}
              className="link-underline font-body text-sm uppercase tracking-[0.2em] text-[var(--text)]/80 hover:text-[var(--text)]"
            >
              {l.label}
            </button>
          ))}
        </div>

        <button
          data-testid="nav-reserve-btn"
          onClick={() => go("#reserve")}
          className="hidden rounded-full border border-accent px-6 py-2 font-body text-xs uppercase tracking-[0.2em] text-accent transition-colors duration-300 hover:bg-accent hover:text-[var(--text)] md:block"
        >
          Visit
        </button>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="text-[var(--text)] md:hidden"
          aria-label="Menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-[var(--line)] bg-black/90 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col px-6 py-6">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  onClick={() => go(l.href)}
                  className="border-b border-[var(--line)] py-4 text-left font-serif-c text-2xl text-[var(--text)]"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-reserve"
                onClick={() => go("#reserve")}
                className="mt-6 rounded-full bg-accent px-6 py-3 font-body text-sm uppercase tracking-[0.2em] text-[var(--text)]"
              >
                Visit Us
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
